// The arithmetic behind the Event Management summary. Pure functions over the
// event records, so the screen only lays the answers out.

import { amountOf, dateOf, isoOf, countBy, totalBy, sumOf, lastMonths, upcoming, inMonth, percent } from '../../utils/summaryStats';
import type { NamedCount, NamedTotal, MonthBucket } from '../../utils/summaryStats';

export interface EventLike {
  eventId?: string;
  eventName?: string;
  category?: string;
  eventDate?: string;
  time?: string;
  endDate?: string;
  venue?: string;
  organiser?: string;
  budget?: unknown;
  /** Expected participants. */
  participants?: unknown;
  actualParticipants?: unknown;
  actualCost?: unknown;
  /** Upcoming / Completed / Cancelled — the lifecycle `status` is ACTIVE/VOID. */
  eventStatus?: string;
  status?: string;
}

/** The page's rule: an explicit status wins; otherwise a past date is Completed. */
export const eventStatusOf = (e: EventLike, now: Date = new Date()): string => {
  if (e.eventStatus) return e.eventStatus;
  const d = dateOf(e.eventDate);
  return d && d < isoOf(now) ? 'Completed' : 'Upcoming';
};

const blank = (v: unknown) => String(v ?? '').trim() === '';

/** A finished event and how it went against its plan. */
export interface EventOutcome {
  event: EventLike;
  budget: number;
  actualCost: number;
  /** Budget minus actual; negative when overspent. Null when no cost was recorded. */
  variance: number | null;
  expected: number;
  actual: number;
}

/** An event the office should look at, and why. */
export interface EventFollowUp {
  event: EventLike;
  reason: string;
}

export interface EventsSummary {
  total: number;
  upcomingCount: number;
  completedCount: number;
  cancelledCount: number;
  thisMonth: number;
  next30: EventLike[];
  byStatus: NamedCount[];
  /** Per category: events, budget (cancelled events left out of the budget). */
  byCategory: NamedTotal[];
  months: MonthBucket[];
  /** Budget of every event that is not cancelled. */
  plannedBudget: number;
  outcomes: EventOutcome[];
  completedBudget: number;
  completedSpent: number;
  overBudget: number;
  /** Actual vs expected participants over completed events that recorded both. */
  expectedTurnout: number;
  actualTurnout: number;
  turnoutPercent: number | null;
  followUps: EventFollowUp[];
}

export const eventsSummary = (rows: EventLike[], now: Date = new Date()): EventsSummary => {
  const events = rows.filter((e) => String(e.status || '').toUpperCase() !== 'VOID');
  const today = isoOf(now);
  const statusOf = (e: EventLike) => eventStatusOf(e, now);
  const live = events.filter((e) => statusOf(e) !== 'Cancelled');
  const completed = events.filter((e) => statusOf(e) === 'Completed');

  const outcomes: EventOutcome[] = completed
    .map((e) => {
      const budget = amountOf(e.budget);
      const actualCost = amountOf(e.actualCost);
      return {
        event: e,
        budget,
        actualCost,
        variance: blank(e.actualCost) ? null : Math.round((budget - actualCost) * 100) / 100,
        expected: amountOf(e.participants),
        actual: amountOf(e.actualParticipants)
      };
    })
    .sort((a, b) => String(dateOf(b.event.eventDate)).localeCompare(String(dateOf(a.event.eventDate))));

  const turnout = outcomes.filter((o) => o.expected > 0 && !blank(o.event.actualParticipants));
  const expectedTurnout = turnout.reduce((n, o) => n + o.expected, 0);
  const actualTurnout = turnout.reduce((n, o) => n + o.actual, 0);

  // Past events still marked Upcoming, and completed ones with no actuals yet.
  const followUps: EventFollowUp[] = [];
  events.forEach((e) => {
    const d = dateOf(e.endDate) || dateOf(e.eventDate) || '';
    if (e.eventStatus === 'Upcoming' && d && d < today) {
      followUps.push({ event: e, reason: 'Date has passed — still marked Upcoming' });
    } else if (statusOf(e) === 'Completed' && blank(e.actualParticipants) && blank(e.actualCost)) {
      followUps.push({ event: e, reason: 'Completed — actual cost & turnout not recorded' });
    }
  });
  followUps.sort((a, b) => String(dateOf(b.event.eventDate)).localeCompare(String(dateOf(a.event.eventDate))));

  return {
    total: events.length,
    upcomingCount: events.filter((e) => statusOf(e) === 'Upcoming').length,
    completedCount: completed.length,
    cancelledCount: events.filter((e) => statusOf(e) === 'Cancelled').length,
    thisMonth: inMonth(live, (e) => e.eventDate, now).length,
    next30: upcoming(live, (e) => e.eventDate, 30, now),
    byStatus: countBy(events, statusOf),
    byCategory: totalBy(events, (e) => e.category, (e) => (statusOf(e) === 'Cancelled' ? 0 : e.budget)),
    months: lastMonths(live, (e) => e.eventDate, 12, now, (e) => e.budget),
    plannedBudget: sumOf(live, (e) => e.budget),
    outcomes,
    completedBudget: sumOf(outcomes, (o) => o.budget),
    completedSpent: sumOf(outcomes, (o) => o.actualCost),
    overBudget: outcomes.filter((o) => o.variance !== null && o.variance < 0).length,
    expectedTurnout,
    actualTurnout,
    turnoutPercent: expectedTurnout > 0 ? percent(actualTurnout, expectedTurnout) : null,
    followUps
  };
};
